import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthToken } from '../auth.ts';
import { getConfig } from '../config.ts';
import { ROUTES } from '../routes.ts';

/**
 * Returns a stable `authFetch` helper that prepends the API base URL and injects
 * a Bearer ID token from the current Amplify session into every request.
 * Stable across renders — safe to use as a useEffect/useCallback dependency
 * (react-router's `useNavigate` is itself stable across renders).
 *
 * A user suspended mid-session only gets caught by RouteGuard's own check on
 * their next navigation — this covers the gap for an API call made without
 * navigating (e.g. sending a message on an already-open page).
 */
export function useAuthFetch() {
  const navigate = useNavigate();

  return useCallback(async function authFetch(path: string, init?: RequestInit): Promise<Response> {
    const token = await getAuthToken();
    const res = await fetch(`${getConfig().api_url}${path}`, {
      ...init,
      headers: {
        ...init?.headers,
        Authorization: `Bearer ${token}`,
      },
    });

    if (res.status === 403) {
      const body = await res.clone().json().catch(() => null) as { error?: string; reason?: string | null } | null;
      if (body?.error === 'account_suspended') {
        navigate(ROUTES.suspended, { replace: true, state: { reason: body.reason ?? null } });
      }
    }

    return res;
  }, [navigate]);
}
